/**
 * Navigation structure templates — used by the App Settings
 * "Navigation Structure" section to bootstrap (or replace) an app's
 * top-level view tree without forcing the author to drag-drop primitives
 * from the palette.
 *
 * Each template seeds a small View hierarchy under `FrontendApp` and
 * points `main_view_id` at its root:
 *
 *   single-screen → Screen "Home"
 *   stack         → StackNavigator > Screen "Home"
 *   tab-3         → TabNavigator   > [Screen "Home", Screen "Search", Screen "Profile"]
 *
 * Mutations are sequential (one round-trip per row) — fine for v1 since
 * each template seeds <= 4 rows. If/when templates get richer we can
 * collapse them into a single nested `insert_View` call.
 */

import { gql } from '../../graphql/client';
import {
  M_DELETE_VIEW_HIERARCHY,
  M_INSERT_VIEW,
  M_INSERT_VIEW_VIEW,
  M_SET_MAIN_VIEW,
} from '../../graphql/operations';
import type { View } from '../../types/forge';

export type Template = 'single-screen' | 'stack' | 'tab-3';

export interface TemplateMeta {
  id: Template;
  title: string;
  description: string;
}

/** UI-friendly template catalog — used by NavigationStructureSection. */
export const TEMPLATES: TemplateMeta[] = [
  {
    id: 'single-screen',
    title: 'Single Screen',
    description: 'One full-screen view. Simplest possible app — good for utilities, prototypes, or when you plan to add navigation later.',
  },
  {
    id: 'stack',
    title: 'Stack Navigator',
    description: 'Push / pop screens with a back button. Pick this when the app is mostly linear: list → detail → action.',
  },
  {
    id: 'tab-3',
    title: 'Tab Bar (3 Tabs)',
    description: 'Bottom tabs with three top-level destinations: Home, Search, Profile. Each tab can later own its own stack.',
  },
];

/* ---------------------------------------------------------------------------
 * Internal seeding helpers
 * ------------------------------------------------------------------------- */

async function insertView(
  appId: string,
  type: string,
  name: string,
  sort: number,
): Promise<string> {
  const res = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
    appId,
    type,
    name,
    sort,
  });
  return res.insert_View_one.id;
}

async function linkChild(
  parentId: string,
  childId: string,
  sort: number,
): Promise<void> {
  await gql(M_INSERT_VIEW_VIEW, { parentId, childId, sort });
}

/* ---------------------------------------------------------------------------
 * Per-template seeders. Each returns the id of the freshly-created root.
 * ------------------------------------------------------------------------- */

async function seedSingleScreen(appId: string): Promise<string> {
  return insertView(appId, 'Screen', 'Home', 0);
}

async function seedStack(appId: string): Promise<string> {
  const stackId = await insertView(appId, 'StackNavigator', 'Main Stack', 0);
  const homeId = await insertView(appId, 'Screen', 'Home', 0);
  await linkChild(stackId, homeId, 0);
  return stackId;
}

async function seedTab3(appId: string): Promise<string> {
  const tabsId = await insertView(appId, 'TabNavigator', 'Main Tabs', 0);
  const homeId = await insertView(appId, 'Screen', 'Home', 0);
  const searchId = await insertView(appId, 'Screen', 'Search', 1);
  const profileId = await insertView(appId, 'Screen', 'Profile', 2);
  await linkChild(tabsId, homeId, 0);
  await linkChild(tabsId, searchId, 1);
  await linkChild(tabsId, profileId, 2);
  return tabsId;
}

async function seed(appId: string, template: Template): Promise<string> {
  switch (template) {
    case 'single-screen': return seedSingleScreen(appId);
    case 'stack':         return seedStack(appId);
    case 'tab-3':         return seedTab3(appId);
  }
}

/* ---------------------------------------------------------------------------
 * Public API
 * ------------------------------------------------------------------------- */

/**
 * Bootstrap an empty app: seeds the chosen template and points
 * `main_view_id` at its root. Caller must ensure the app currently
 * has no `main_view_id` (the UI hides this CTA otherwise).
 *
 * Returns the new root view id so the caller can deep-link the user
 * into the View Builder pre-selected on the new root.
 */
export async function applyTemplate(
  appId: string,
  template: Template,
): Promise<string> {
  const rootId = await seed(appId, template);
  await gql(M_SET_MAIN_VIEW, { appId, viewId: rootId });
  return rootId;
}

/**
 * Destructive: deletes the existing root subtree, seeds a new template,
 * and re-points `main_view_id`. The deletion uses
 * `delete_view_hierarchy(view_id)` (a Postgres function) which cascades
 * through `View_View` and removes every descendant View row + its
 * ViewStyle / ViewSource / interactions in one call.
 *
 * Caller is expected to have shown a confirmation dialog before invoking
 * this — we do not double-confirm here.
 */
export async function replaceRoot(
  appId: string,
  oldRootId: string,
  template: Template,
): Promise<string> {
  await gql(M_DELETE_VIEW_HIERARCHY, { viewId: oldRootId });
  const newRootId = await seed(appId, template);
  await gql(M_SET_MAIN_VIEW, { appId, viewId: newRootId });
  return newRootId;
}
